// packages/api-client/src/services/fsService.ts

import { getApiClient } from '../../client';
import { handleApiError } from '../../utils/handleApiError';
import type { TreeNode, FileGetResponse, FilePutRequest, DiffResponse } from '@dopomogai/types';
import type { KnowledgeProjectSummary, FSTreeNode, FSResolveResponse } from '@dopomogai/types';

// --- Types for New Cloud-Native FS ---
export interface FileSaveRequest {
  project_id?: string;
  file_path: string;
  content: string;
  agent_id?: string;
  conversation_id?: string;
  commit_message?: string;
}

export interface FileSaveResponse {
  id: string;
  version: number;
  file_path: string;
  head_file_id: string;
  updated_at: string;
  project_id: string;
}

export interface KBLinkRequest {
  project_id?: string;
  file_path: string;
  uploaded_file_id: string;
  commit_message?: string;
}

export interface KBLinkResponse {
  id: string;
  project_id: string;
  file_path: string;
  head_file_id: string;
  version: number;
  updated_at: string;
}

type SR<T> = Promise<T | import('@dopomogai/types').ApiErrorResponse>;

// --- 1. Legacy Physical FS (Used by Agent Kit / Repo Browser) ---

const tree = (root: string): SR<TreeNode[]> =>
  getApiClient()
    .get('/fs/tree', { params: { root } })
    .then((res) => res.data)
    .catch((err) => handleApiError(err, `listing tree for ${root}`));

const getFile = (path: string): SR<FileGetResponse> =>
  getApiClient()
    .get('/fs/file', { params: { path } })
    .then((res) => res.data)
    .catch((err) => handleApiError(err, `fetching file ${path}`));

const putFile = (payload: FilePutRequest): SR<{ ok: boolean; path: string; mtime: number }> =>
  getApiClient()
    .post('/fs/file', payload)
    .then((res) => res.data)
    .catch((err) => handleApiError(err, `writing file ${payload.path}`));

const diff = (path: string, content: string): SR<DiffResponse> =>
  getApiClient()
    .post('/fs/diff', { path, content })
    .then((res) => res.data)
    .catch((err) => handleApiError(err, `diff for ${path}`));

// --- 2. New Cloud-Native FS (KB) ---

const saveFile = async (payload: FileSaveRequest): Promise<FileSaveResponse | null> => {
  return getApiClient()
    .post('/fs/save', payload)
    .then((res) => res.data)
    .catch((err) => handleApiError(err, 'saving file to cloud'));
};

const listProjects = async (): Promise<any> => {
  return getApiClient()
    .get('/fs/projects')
    .then((res) => res.data)
    .catch((err) => handleApiError(err, 'fetching projects'));
};

const getProjectTree = async (projectId?: string): Promise<FSTreeNode[] | null> => {
  const params = projectId ? { project_id: projectId } : {};
  return getApiClient()
    .get('/fs/tree', { params })
    .then((res) => res.data)
    .catch((err) => handleApiError(err, 'fetching KB tree'));
};

const linkUploadedFileToKB = async (payload: KBLinkRequest): Promise<KBLinkResponse | null> => {
  return getApiClient()
    .post('/fs/link', payload)
    .then((res) => res.data)
    .catch((err) => handleApiError(err, 'linking uploaded file to KB path'));
};

const resolvePath = async (project_id: string, path: string): Promise<FSResolveResponse | null> => {
  return getApiClient()
    .get('/fs/resolve', { params: { project_id, path } })
    .then((res) => res.data)
    .catch((err) => handleApiError(err, 'resolving KB path'));
};

export const fsService = {
  // Legacy
  tree,
  getFile,
  putFile,
  diff,

  // KB
  saveFile,
  listProjects,
  getProjectTree,
  linkUploadedFileToKB,
  resolve: resolvePath
};